import * as React from "react";
import { format, parse, isValid, setMonth, setYear, getMonth, getYear } from "date-fns";
import { CalendarBlank } from "@phosphor-icons/react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  minDate,
  maxDate,
  disabled = false,
  className,
  yearRange,
  "data-testid": testId,
}) {
  const [open, setOpen] = React.useState(false);

  // Parse the YYYY-MM-DD string to a Date object
  const dateValue = React.useMemo(() => {
    if (!value) return undefined;
    const d = parse(value, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : undefined;
  }, [value]);

  // Month shown in calendar
  const [displayMonth, setDisplayMonth] = React.useState(
    dateValue || new Date()
  );

  React.useEffect(() => {
    if (dateValue) setDisplayMonth(dateValue);
  }, [dateValue]);

  // Year range for dropdown
  const currentYear = new Date().getFullYear();
  const startYear = yearRange?.[0] || 1930;
  const endYear = yearRange?.[1] || currentYear;
  const years = React.useMemo(() => {
    const arr = [];
    for (let y = endYear; y >= startYear; y--) arr.push(y);
    return arr;
  }, [startYear, endYear]);

  const handleSelect = (day) => {
    if (day) {
      onChange(format(day, "yyyy-MM-dd"));
      setOpen(false);
    }
  };

  const handleMonthChange = (e) => {
    const m = parseInt(e.target.value, 10);
    setDisplayMonth((prev) => setMonth(prev, m));
  };

  const handleYearChange = (e) => {
    const y = parseInt(e.target.value, 10);
    setDisplayMonth((prev) => setYear(prev, y));
  };

  const minDateObj = minDate ? parse(minDate, "yyyy-MM-dd", new Date()) : undefined;
  const maxDateObj = maxDate ? parse(maxDate, "yyyy-MM-dd", new Date()) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-start text-left font-normal rounded-sm h-9 px-3 border-input",
            !dateValue && "text-muted-foreground",
            className
          )}
        >
          <CalendarBlank className="mr-2 h-4 w-4 opacity-60" />
          {dateValue ? format(dateValue, "dd MMM yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {/* Month/Year Selector */}
        <div className="flex items-center gap-2 px-3 pt-3 pb-1">
          <select
            value={getMonth(displayMonth)}
            onChange={handleMonthChange}
            className="flex-1 h-8 rounded-sm border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i}>{m}</option>
            ))}
          </select>
          <select
            value={getYear(displayMonth)}
            onChange={handleYearChange}
            className="w-[90px] h-8 rounded-sm border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          month={displayMonth}
          onMonthChange={setDisplayMonth}
          disabled={(date) => {
            if (minDateObj && date < minDateObj) return true;
            if (maxDateObj && date > maxDateObj) return true;
            return false;
          }}
          initialFocus
        />
        {dateValue && (
          <div className="px-3 pb-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full text-xs text-muted-foreground"
              onClick={() => { onChange(""); setOpen(false); }}
            >
              Clear date
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export { DatePicker };
